'use client'

import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const CTA: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [error, setError] = useState('')
    const [submitted, setSubmitted] = useState(false)

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!EMAIL_RE.test(email)) {
            setError('Please enter a valid email address.')
            return
        }
        setError('')
        setSubmitted(true)
    }

    return (
        <section
            id='waitlist'
            className='scroll-mt-20 bg-[#0a0a0c] px-4 py-24 text-white md:px-6 md:py-32'
        >
            <div className='mx-auto max-w-3xl text-center'>
                <span className='text-xs font-semibold uppercase tracking-[0.18em] text-white/40'>
                    Early access
                </span>
                <h2
                    className='mt-5 text-4xl tracking-tight sm:text-5xl md:text-6xl'
                    style={{ fontFamily: "'Caveat Brush', cursive", lineHeight: 0.95 }}
                >
                    Queue up. Level up.
                </h2>
                <p className='mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60 md:text-lg'>
                    Free while we are in early access. Drop your email and get your first
                    shelf the moment your spot opens.
                </p>

                {submitted ? (
                    <div className='mx-auto mt-10 flex max-w-md items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-6 py-5'>
                        <span className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#0a0a0c]'>
                            <Check className='size-4' />
                        </span>
                        <p className='text-left text-sm text-white/80'>
                            You are on the list. We will email{' '}
                            <span className='font-semibold text-white'>{email}</span> when your
                            first shelf is ready.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        noValidate
                        className='mx-auto mt-10 flex max-w-md flex-col gap-3 sm:flex-row'
                    >
                        <div className='flex-1 text-left'>
                            <label htmlFor='waitlist-email' className='sr-only'>
                                Email address
                            </label>
                            <input
                                id='waitlist-email'
                                type='email'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder='you@studio.com'
                                className='w-full rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none'
                            />
                            {error && (
                                <p className='mt-2 pl-2 text-xs text-red-400'>{error}</p>
                            )}
                        </div>
                        <button
                            type='submit'
                            className='flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0a0a0c] transition-transform hover:-translate-y-0.5'
                        >
                            Get early access
                            <ArrowRight className='size-4' />
                        </button>
                    </form>
                )}

                <p className='mt-5 text-xs text-white/35'>
                    No spam. One email when your shelf is ready, nothing else.
                </p>
            </div>
        </section>
    )
}
